import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Euro, ShoppingBag, Users, Package, Clock, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { StatusBadge } from "@/pages/AccountOrders";

export const AdminDashboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((d) => setData(d.data)).catch(() => {});
  }, []);

  if (!data) return <div className="h-40 bg-ink animate-pulse" />;

  const stats = [
    ["Omzet (totaal)", `€ ${data.revenue.toFixed(2).replace(".", ",")}`, <Euro size={15} key="1" />, "dash-revenue"],
    ["Bestellingen", data.orders_count, <ShoppingBag size={15} key="2" />, "dash-orders"],
    ["Klanten", data.customers, <Users size={15} key="3" />, "dash-customers"],
    ["Producten", data.products_count, <Package size={15} key="4" />, "dash-products"],
    ["Wachten op verzending", data.pending, <Clock size={15} key="5" />, "dash-pending"],
    ["Open retouren", data.returns_open, <RotateCcw size={15} key="6" />, "dash-returns"],
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif font-light text-cream text-3xl mb-8">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        {stats.map(([label, value, icon, testid]) => (
          <div key={label} data-testid={testid} className="border border-gold/15 bg-ink p-5">
            <span className="text-gold/80">{icon}</span>
            <p className="mt-3 font-serif text-2xl text-cream">{value}</p>
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-smoke mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid xl:grid-cols-2 gap-8">
        <div className="border border-gold/15 bg-ink p-6">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-6">Omzet — laatste 14 dagen</h2>
          <div data-testid="sales-chart" className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series}>
                <CartesianGrid stroke="rgba(212,175,55,0.08)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#5C606B", fontSize: 9 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#5C606B", fontSize: 9 }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={{ background: "#171A21", border: "1px solid rgba(212,175,55,0.3)", fontFamily: "monospace", fontSize: 11 }} formatter={(v) => `€ ${v}`} />
                <Bar dataKey="revenue" fill="#D4AF37" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-gold/15 bg-ink p-6">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-6">Laatste bestellingen</h2>
          <div className="space-y-3">
            {data.recent_orders.map((o) => (
              <Link key={o.order_id} to="/admin/bestellingen" data-testid={`dash-order-${o.order_id}`} className="flex items-center justify-between border-b border-gold/5 pb-3 hover:text-gold transition-colors">
                <div>
                  <p className="font-mono text-xs text-cream">{o.order_id}</p>
                  <p className="font-mono text-[10px] text-smoke mt-1">{o.name} · {o.items?.length} artikelen</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-xs text-cream">€ {o.total?.toFixed(2).replace(".", ",")}</span>
                  <StatusBadge status={o.status} />
                </div>
              </Link>
            ))}
            {data.recent_orders.length === 0 && <p className="text-sm text-mist">Nog geen bestellingen.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
